import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { Gift, Coins, TrendingUp, Check, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function AdminGifts() {
  const [data, setData] = useState(null);
  const load = () => adminClient().get("/admin/gifts/overview").then(({ data }) => setData(data)).catch(() => setData({ transactions: [], payouts: [], catalog: [], fees: {} }));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const saveFee = async (role, val) => { const fees = { ...data.fees, [role]: Number(val) / 100 }; try { await adminClient().put("/admin/gifts/fees", { fees }); setData((d) => ({ ...d, fees })); toast.success("Fee updated."); } catch { toast.error("Failed."); } };
  const toggleGift = async (key, enabled) => { try { await adminClient().put(`/admin/gifts/catalog/${key}`, { enabled }); load(); } catch { toast.error("Failed."); } };
  const setPayout = async (id, status) => { try { await adminClient().put(`/admin/gifts/payouts/${id}`, { status }); toast.success(`Payout ${status}.`); load(); } catch { toast.error("Failed."); } };
  const refund = async (id) => { try { await adminClient().post(`/admin/gifts/transactions/${id}/refund`); toast.success("Gift reversed."); load(); } catch { toast.error("Failed."); } };

  return (
    <div data-testid="admin-gifts">
      <div className="flex items-center gap-2 mb-1"><Gift className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Live Gifts & TAVEN Coins</h1></div>
      <p className="text-sm text-muted-foreground mb-6">Gifts are voluntary digital support items, separate from all product/course/affiliate/certificate payments. Payouts require your approval + fraud review.</p>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="border border-black/10 bg-white px-4 py-3"><div className="flex items-center gap-2 text-muted-foreground"><Gift className="w-4 h-4 text-taven-gold" /><p className="text-xs uppercase tracking-wider">Gifts Sent</p></div><p className="text-2xl font-black font-display">{data.total_gifts}</p></div>
        <div className="border border-black/10 bg-white px-4 py-3"><div className="flex items-center gap-2 text-muted-foreground"><Coins className="w-4 h-4 text-taven-gold" /><p className="text-xs uppercase tracking-wider">Coins Gifted</p></div><p className="text-2xl font-black font-display">{data.total_coins}</p></div>
      </div>

      <h2 className="text-sm font-black uppercase tracking-wider mb-2">Platform Fees by Host Type</h2>
      <div className="flex flex-wrap gap-3 mb-8">
        {["seller", "instructor", "affiliate", "partner", "admin"].map((r) => (
          <div key={r} className="border border-black/10 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground capitalize">{r}</p>
            <div className="flex items-center gap-1"><input type="number" defaultValue={Math.round((data.fees[r] || 0) * 100)} onBlur={(e) => saveFee(r, e.target.value)} data-testid={`fee-${r}`} className="w-14 border border-black/15 px-2 py-1 text-sm" /><span className="text-sm">%</span></div>
          </div>
        ))}
      </div>

      <h2 className="text-sm font-black uppercase tracking-wider mb-2">Gift Catalog</h2>
      <div className="flex flex-wrap gap-2 mb-8">
        {data.catalog.map((g) => (
          <button key={g.key} onClick={() => toggleGift(g.key, !g.enabled)} data-testid={`toggle-gift-${g.key}`} className={`flex items-center gap-1.5 px-3 py-1.5 border text-xs font-bold ${g.enabled ? "border-taven-gold bg-taven-gold/10" : "border-black/10 opacity-50"}`}>
            <span className="text-base">{g.emoji}</span> {g.name} · {g.coins}c {g.enabled ? "" : "(off)"}
          </button>
        ))}
      </div>

      <h2 className="text-sm font-black uppercase tracking-wider mb-2">Payout Requests</h2>
      <div className="border border-black/10 bg-white overflow-x-auto mb-8">
        <table className="w-full text-sm">
          <thead className="bg-taven-gray/60 text-left"><tr><th className="px-4 py-3 font-bold">Host</th><th className="px-4 py-3 font-bold">Coins</th><th className="px-4 py-3 font-bold">Status</th><th className="px-4 py-3 font-bold text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-black/5">
            {data.payouts.map((p) => (
              <tr key={p.id}>
                <td className="px-4 py-3">{p.host_email}</td><td className="px-4 py-3">{p.amount}</td>
                <td className="px-4 py-3 capitalize font-bold">{p.status}</td>
                <td className="px-4 py-3 text-right">
                  {p.status === "pending" && <div className="flex justify-end gap-2">
                    <button onClick={() => setPayout(p.id, "approved")} data-testid={`payout-approve-${p.id}`} className="p-1.5 bg-green-600 text-white"><Check className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setPayout(p.id, "rejected")} className="p-1.5 bg-red-600 text-white"><X className="w-3.5 h-3.5" /></button>
                  </div>}
                  {p.status === "approved" && <button onClick={() => setPayout(p.id, "paid")} className="text-xs font-bold text-taven-gold">Mark Paid</button>}
                </td>
              </tr>
            ))}
            {data.payouts.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No payout requests.</td></tr>}
          </tbody>
        </table>
      </div>

      <h2 className="text-sm font-black uppercase tracking-wider mb-2">Recent Gift Transactions</h2>
      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-taven-gray/60 text-left"><tr><th className="px-4 py-3 font-bold">From</th><th className="px-4 py-3 font-bold">Gift</th><th className="px-4 py-3 font-bold">Host</th><th className="px-4 py-3 font-bold">Earned</th><th className="px-4 py-3 font-bold">Status</th><th className="px-4 py-3 font-bold text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-black/5">
            {data.transactions.map((t) => (
              <tr key={t.id}>
                <td className="px-4 py-3">{t.from_name}</td>
                <td className="px-4 py-3">{t.emoji} {t.gift_name} ({t.coins}c)</td>
                <td className="px-4 py-3 text-muted-foreground">{t.host_email}</td>
                <td className="px-4 py-3">{t.host_earnings}c</td>
                <td className="px-4 py-3"><span className={t.status === "refunded" ? "text-red-600 font-bold" : "text-green-700"}>{t.status}</span></td>
                <td className="px-4 py-3 text-right">{t.status === "active" && <button onClick={() => refund(t.id)} data-testid={`refund-${t.id}`} className="p-1.5 bg-red-600 text-white inline-flex" title="Reverse"><RotateCcw className="w-3.5 h-3.5" /></button>}</td>
              </tr>
            ))}
            {data.transactions.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No gifts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
